// Two calendars. "Wall Street" is the NYSE cash session (09:30 to 16:00 ET, 13:00 on early-close days).
// The Chainlink Stock Token feeds do not follow it: they follow Robinhood's 24 hour session, Sunday
// 20:00 ET to Friday 20:00 ET. Each trading day D owns the session from D-1 20:00 ET to D 20:00 ET; a
// US market holiday drops its session, so after a holiday the feed resumes at 20:00 ET on the holiday
// itself (observed on Labor Day 2026-09-07: the feeds resumed at 2026-09-08 00:00 UTC). Whether the
// session ends earlier on NYSE early-close days is unverified; this model keeps 20:00 ET.

export const CLOSED: ReadonlySet<string> = new Set([
  '2026-01-01', '2026-01-19', '2026-02-16', '2026-04-03', '2026-05-25', '2026-06-19', '2026-07-03', '2026-09-07', '2026-11-26', '2026-12-25',
  '2027-01-01', '2027-01-18', '2027-02-15', '2027-03-26', '2027-05-31', '2027-06-18', '2027-07-05', '2027-09-06', '2027-11-25', '2027-12-24',
]);
export const EARLY: ReadonlySet<string> = new Set(['2026-11-27', '2026-12-24', '2027-11-26', '2027-12-23']);
const SESSION_END = 20 * 60; // minutes after midnight ET
const CASH_OPEN = 9 * 60 + 30;

const etFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', weekday: 'short',
});

interface EtParts { ymd: string; wd: string; min: number }

export function etParts(date: Date): EtParts {
  const p: Record<string, string> = {};
  for (const x of etFmt.formatToParts(date)) p[x.type] = x.value;
  const year = p.year ?? '', month = p.month ?? '', day = p.day ?? '';
  return { ymd: `${year}-${month}-${day}`, wd: p.weekday ?? '', min: Number(p.hour) * 60 + Number(p.minute) };
}

function ymdParts(ymd: string): [number, number, number] {
  const [y = 0, m = 0, d = 0] = ymd.split('-').map(Number);
  return [y, m, d];
}

/** The UTC instant of `minutes` after midnight ET on the day `ymd` (DST-aware). */
export function etToUtc(ymd: string, minutes: number): Date {
  const [y, m, d] = ymdParts(ymd);
  let guess = Date.UTC(y, m - 1, d, Math.floor(minutes / 60) + 4, minutes % 60);
  for (let i = 0; i < 2; i++) {
    const p = etParts(new Date(guess));
    const [py, pm, pd] = ymdParts(p.ymd);
    const diff = (Date.UTC(py, pm - 1, pd) + p.min * 60000) - (Date.UTC(y, m - 1, d) + minutes * 60000);
    guess -= diff;
  }
  return new Date(guess);
}

export const isTradingDay = (ymd: string, wd: string): boolean => wd !== 'Sat' && wd !== 'Sun' && !CLOSED.has(ymd);
export const closeMinutes = (ymd: string): number => (EARLY.has(ymd) ? 13 * 60 : 16 * 60);
export function shiftDay(ymd: string, n: number): string {
  const d = new Date(ymd + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
export const weekdayOf = (ymd: string): string =>
  new Date(ymd + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });

export type MarketState =
  | { open: true; closesAt: Date }
  | { open: false; closedAt: Date; opensAt: Date };

export function marketState(now: Date): MarketState {
  const p = etParts(now);
  const open = isTradingDay(p.ymd, p.wd) && p.min >= CASH_OPEN && p.min < closeMinutes(p.ymd);
  if (open) return { open: true, closesAt: etToUtc(p.ymd, closeMinutes(p.ymd)) };
  // last close
  let d = p.ymd;
  if (!(isTradingDay(p.ymd, p.wd) && p.min >= closeMinutes(p.ymd))) {
    do { d = shiftDay(d, -1); } while (!isTradingDay(d, weekdayOf(d)));
  }
  const closedAt = etToUtc(d, closeMinutes(d));
  // next open
  let n = p.ymd;
  if (!(isTradingDay(p.ymd, p.wd) && p.min < CASH_OPEN)) {
    do { n = shiftDay(n, 1); } while (!isTradingDay(n, weekdayOf(n)));
  }
  return { open: false, closedAt, opensAt: etToUtc(n, CASH_OPEN) };
}

export type FeedSession =
  | { live: true; startedAt: Date; endsAt: Date; api: boolean }
  | { live: false; frozenSince: Date; resumesAt: Date; reason: string | null; api: boolean };

export function feedSession(now: Date): FeedSession {
  const p = etParts(now);
  // The trading day that owns this moment: today until 20:00 ET, tomorrow after.
  const owner = p.min < SESSION_END ? p.ymd : shiftDay(p.ymd, 1);
  if (isTradingDay(owner, weekdayOf(owner))) {
    return { live: true, startedAt: etToUtc(shiftDay(owner, -1), SESSION_END), endsAt: etToUtc(owner, SESSION_END), api: false };
  }
  let d = shiftDay(owner, -1);
  while (!isTradingDay(d, weekdayOf(d))) d = shiftDay(d, -1);
  let n = shiftDay(owner, 1);
  while (!isTradingDay(n, weekdayOf(n))) n = shiftDay(n, 1);
  return { live: false, frozenSince: etToUtc(d, SESSION_END), resumesAt: etToUtc(shiftDay(n, -1), SESSION_END), reason: null, api: false };
}
